import dayjs, {Dayjs} from 'dayjs';
import {useMemo, useState} from 'react';
import styled, {useTheme} from 'styled-components';
import {theme as defaultTheme} from '@ui/style/theme';
import {getColorFromTheme} from '@ui/ui-utils';
import {metricProgress} from '@utils/proportions';

interface ActivityPoint {
  currentValue?: number;
  endDate?: string;
  isGoalEndDate?: boolean;
  isTodayDate?: boolean;
  percentageProgress?: number | string;
}

interface GoalProgressChartProps {
  activities?: ActivityPoint[];
  currentProgress: number;
  endDate: string;
  startDate: string;
  startValue?: number;
  targetValue?: number;
}

interface ChartPoint {
  date: Dayjs;
  progress: number;
}

const CHART_WIDTH = 520;
const CHART_HEIGHT = 180;
const PLOT = {top: 20, right: 506, bottom: 154, left: 38};
const TOOLTIP_HEIGHT = 30;
const TOOLTIP_MARGIN = 9;
const TOOLTIP_SIDE_PADDING = 10;

const Chart = styled.figure`
  width: 100%;
  margin: 0;
  padding: 0 16px 14px;
  color: ${getColorFromTheme('textBody')};
  font-family: Graphik, sans-serif;
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 2px 0 6px;
  font-size: 11px;
  line-height: 18px;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const LegendLine = styled.i<{$color: string; $dashed?: boolean}>`
  display: block;
  width: 16px;
  border-top: 2px ${({$dashed}) => ($dashed ? 'dashed' : 'solid')}
    ${({$color}) => $color};
`;

const Svg = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;

  text {
    font-family: Graphik, sans-serif;
  }
`;

const Dates = styled.figcaption`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-top: 4px;
  color: ${getColorFromTheme('textBody')};
  font-size: 13px;
  line-height: 20px;

  strong {
    color: ${getColorFromTheme('textMedium')};
    font-weight: 600;
  }
`;

const clamp = (value: number) => Math.min(100, Math.max(0, value));

const pointLabel = (point: ChartPoint) =>
  `${point.date.format('D MMM YYYY')}: ${Math.round(point.progress)}%`;

const tooltipPosition = (label: string, pointX: number, pointY: number) => {
  const width = Math.max(92, label.length * 6.2 + TOOLTIP_SIDE_PADDING * 2);
  const x = Math.min(CHART_WIDTH - width - 4, Math.max(4, pointX - width / 2));
  const placeBelow = pointY - TOOLTIP_HEIGHT - TOOLTIP_MARGIN < 0;
  const y = placeBelow
    ? pointY + TOOLTIP_MARGIN
    : pointY - TOOLTIP_HEIGHT - TOOLTIP_MARGIN;

  return {height: TOOLTIP_HEIGHT, placeBelow, width, x, y};
};

const activityProgress = (
  activity: ActivityPoint,
  startValue?: number,
  targetValue?: number,
): number | undefined => {
  const currentValue = Number(activity.currentValue);
  const start = Number(startValue);
  const target = Number(targetValue);
  if (
    Number.isFinite(currentValue) &&
    Number.isFinite(start) &&
    Number.isFinite(target)
  ) {
    return metricProgress(start, currentValue, target);
  }

  if (
    activity.percentageProgress === undefined ||
    activity.percentageProgress === null ||
    activity.percentageProgress === ''
  ) {
    return undefined;
  }

  const supplied = Number(String(activity.percentageProgress).replace('%', ''));
  if (Number.isFinite(supplied)) return clamp(supplied);
  return undefined;
};

const validDate = (value: string | undefined) => {
  const date = dayjs(value);
  return date.isValid() ? date : undefined;
};

const chartPoints = ({
  activities = [],
  currentProgress,
  startDate,
  endDate,
  startValue,
  targetValue,
}: Pick<
  GoalProgressChartProps,
  | 'activities'
  | 'currentProgress'
  | 'startDate'
  | 'endDate'
  | 'startValue'
  | 'targetValue'
>) => {
  const today = dayjs();
  const activityDates = activities
    .map((activity) => validDate(activity.endDate))
    .filter((date): date is Dayjs => Boolean(date));
  const start =
    validDate(startDate)?.startOf('day') || activityDates[0] || today;
  const suppliedEnd = validDate(endDate);
  const normalizedEnd = suppliedEnd?.endOf('day');
  const end = normalizedEnd?.isAfter(start)
    ? normalizedEnd
    : start.add(1, 'day');
  const progressDate = today.isBefore(start)
    ? start
    : today.isAfter(end)
    ? end
    : today;

  const pointsByTimestamp = new Map<number, ChartPoint>();
  activities.forEach((activity) => {
    if (activity.isGoalEndDate || activity.isTodayDate) return;
    const date = validDate(activity.endDate);
    const progress = activityProgress(activity, startValue, targetValue);
    if (
      !date ||
      progress === undefined ||
      date.isBefore(start) ||
      date.isAfter(progressDate)
    ) {
      return;
    }
    pointsByTimestamp.set(date.valueOf(), {date, progress});
  });

  const points = Array.from(pointsByTimestamp.values()).sort(
    (a, b) => a.date.valueOf() - b.date.valueOf(),
  );
  if (!pointsByTimestamp.has(start.valueOf())) {
    points.unshift({date: start, progress: 0});
  }

  const latestProgress = clamp(
    Number.isFinite(Number(currentProgress))
      ? Number(currentProgress)
      : points[points.length - 1]?.progress || 0,
  );
  const existingToday = points.find((point) => point.date.isSame(progressDate));
  if (existingToday) existingToday.progress = latestProgress;
  else points.push({date: progressDate, progress: latestProgress});

  return {start, end, progressDate, points};
};

export const GoalProgressChart = ({
  activities,
  currentProgress,
  endDate,
  startDate,
  startValue,
  targetValue,
}: GoalProgressChartProps) => {
  const activeTheme = useTheme() || defaultTheme;
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const {start, end, progressDate, points} = useMemo(
    () =>
      chartPoints({
        activities,
        currentProgress,
        endDate,
        startDate,
        startValue,
        targetValue,
      }),
    [activities, currentProgress, endDate, startDate, startValue, targetValue],
  );
  const actualColor = activeTheme.colors.purple300;
  const chartColors = activeTheme.colors;
  const duration = Math.max(1, end.diff(start, 'millisecond'));
  const x = (date: Dayjs) =>
    PLOT.left +
    ((date.valueOf() - start.valueOf()) / duration) * (PLOT.right - PLOT.left);
  const y = (progress: number) =>
    PLOT.bottom - (clamp(progress) / 100) * (PLOT.bottom - PLOT.top);
  const linePath = points
    .map(
      (point, index) =>
        `${index === 0 ? 'M' : 'L'} ${x(point.date)} ${y(point.progress)}`,
    )
    .join(' ');
  const latest = points[points.length - 1];
  const latestX = x(latest.date);
  const latestY = y(latest.progress);
  const labelOnLeft = latestX > PLOT.right - 40;
  const labelBelow = latestY < PLOT.top + 14;

  return (
    <Chart aria-label="Goal progress against target pace">
      <Legend aria-hidden="true">
        <LegendItem>
          <LegendLine $color={actualColor} /> Actual
        </LegendItem>
        <LegendItem>
          <LegendLine $color={chartColors.textMuted} $dashed /> Target pace
        </LegendItem>
      </Legend>

      <Svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        role="img"
        aria-label={`Current progress ${Math.round(
          latest.progress,
        )} percent; target 100 percent by ${end.format('D MMM YYYY')}`}>
        {[100, 50, 0].map((tick) => (
          <g key={tick}>
            <line
              x1={PLOT.left}
              x2={PLOT.right}
              y1={y(tick)}
              y2={y(tick)}
              stroke={chartColors.borderLight}
              strokeWidth="1"
            />
            <text
              x={PLOT.left - 10}
              y={y(tick) + 4}
              fill={chartColors.textMuted}
              fontSize="11"
              textAnchor="end">
              {tick === 0 ? '0' : `${tick}%`}
            </text>
          </g>
        ))}

        <line
          x1={PLOT.left}
          x2={PLOT.right}
          y1={PLOT.bottom}
          y2={PLOT.top}
          stroke={chartColors.textMuted}
          strokeDasharray="4 5"
          strokeWidth="2"
        />

        <line
          x1={x(progressDate)}
          x2={x(progressDate)}
          y1={PLOT.top - 2}
          y2={PLOT.bottom}
          stroke={chartColors.borderDark}
          strokeDasharray="3 4"
          strokeWidth="1"
        />
        <text
          x={x(progressDate)}
          y={PLOT.top - 8}
          fill={chartColors.textMuted}
          fontSize="10"
          textAnchor="middle">
          Today
        </text>

        <path
          data-testid="actual-progress-line"
          d={linePath}
          fill="none"
          stroke={actualColor}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.5"
        />

        {points.map((point, index) => {
          const isLatest = index === points.length - 1;
          const label = pointLabel(point);
          return (
            <g
              key={`${point.date.valueOf()}-${index}`}
              aria-label={label}
              tabIndex={0}
              onBlur={() => setHoveredPoint(null)}
              onFocus={() => setHoveredPoint(index)}
              onMouseEnter={() => setHoveredPoint(index)}
              onMouseLeave={() => setHoveredPoint(null)}>
              <circle
                cx={x(point.date)}
                cy={y(point.progress)}
                r="11"
                fill="transparent"
                stroke="none"
              />
              <circle
                cx={x(point.date)}
                cy={y(point.progress)}
                r={isLatest ? 5 : 3.5}
                fill={isLatest ? actualColor : chartColors.white}
                stroke={actualColor}
                strokeWidth="2"
              />
            </g>
          );
        })}

        {hoveredPoint !== null &&
          (() => {
            const point = points[hoveredPoint];
            const pointX = x(point.date);
            const pointY = y(point.progress);
            const label = pointLabel(point);
            const tooltip = tooltipPosition(label, pointX, pointY);
            const arrowBaseY = tooltip.placeBelow
              ? tooltip.y
              : tooltip.y + tooltip.height;
            const arrowTipY = tooltip.placeBelow ? pointY + 4 : pointY - 4;

            return (
              <g role="tooltip" pointerEvents="none">
                <rect
                  data-testid="progress-point-tooltip-background"
                  x={tooltip.x}
                  y={tooltip.y}
                  width={tooltip.width}
                  height={tooltip.height}
                  rx="6"
                  fill={chartColors.textDark}
                />
                <path
                  d={`M ${pointX - 5} ${arrowBaseY} L ${
                    pointX + 5
                  } ${arrowBaseY} L ${pointX} ${arrowTipY} Z`}
                  fill={chartColors.textDark}
                />
                <text
                  x={tooltip.x + tooltip.width / 2}
                  y={tooltip.y + 19}
                  fill={chartColors.white}
                  fontSize="11"
                  fontWeight="500"
                  textAnchor="middle">
                  {label}
                </text>
              </g>
            );
          })()}

        <text
          x={latestX + (labelOnLeft ? -9 : 9)}
          y={latestY + (labelBelow ? 16 : -8)}
          fill={actualColor}
          fontSize="12"
          fontWeight="600"
          textAnchor={labelOnLeft ? 'end' : 'start'}>
          {Math.round(latest.progress)}%
        </text>
      </Svg>

      <Dates>
        <span>
          Start date: <strong>{start.format('D MMM YYYY')}</strong>
        </span>
        <span>
          End date: <strong>{end.format('D MMM YYYY')}</strong>
        </span>
      </Dates>
    </Chart>
  );
};
